import {Component, Input, NgModule, OnChanges, OnDestroy, QueryList, SimpleChanges, ViewChildren} from '@angular/core';
import {
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    FormsModule,
    ReactiveFormsModule,
    FormArray
} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {IonicModule} from '@ionic/angular';
import {CardModule} from '../../../../modules/core/component/card/card.module';
import {FieldInputModule} from '../../field/field-input/field-input.module';
import {ContentDirective} from '../../../../shared/directives/content.directive';
import {FormFieldModule} from '../../../../modules/core/component/form-field/form-field.module';
import {FieldInputComponent} from '../../field/field-input/field-input.component';
import {
    FormDefinitionGroupWithValuesModel
} from '../../../../modules/form/definition/group/api/entity/formDefinitionGroupWithValues.model';
import {FormValueModel} from '../../../../modules/form/form/value/api/entity/formValue.model';
import {
    FormFormSaveComponentService
} from '../../../../modules/form/form/api/component/form-form-save/form-form-save-component.service';
import {Subscription} from 'rxjs';
import {PlatformControl} from "../../../../controls/core/platform/platform.control";
import {PartnerSummaryModel} from "../../../../modules/partner/common/api/model/partnerSummary.model";
import {DatePickerModule} from "../../date-picker/date-picker.module";
import {MatTooltipModule} from "@angular/material/tooltip";

@Component({
    selector: 'app-group-fields-input',
    template: `
        <ng-container *ngIf="displayType === 'table' else defaultTmpl">
            <div class="d-flex gap-4 m-t-4">
                <div class="flex-shrink-0 flex-grow-0" style="width: 4px; border-radius: 4px; min-height: 100px;"
                     [style.background]="groupModel.definition.color ? groupModel.definition.color : 'transparent'">
                </div>
                <div class="flex-grow-1">
                    <div
                            class="form-array-grid m-t-4"
                            [ngStyle]="{
    '--cols-xs': Math.min(groupModel.definition.nbColumns, 1),
    '--cols-sm': Math.min(groupModel.definition.nbColumns, 1),
    '--cols-md': Math.min(groupModel.definition.nbColumns, 2),
    '--cols-lg': Math.min(groupModel.definition.nbColumns, 2),
    '--cols-xl': Math.min(groupModel.definition.nbColumns, 3),
    '--cols-xxl': Math.min(groupModel.definition.nbColumns, 4)
  }"
                            [style.padding-left.px]="4">
                        <ng-container *ngFor="let field of fields; index as i">
                            <div class="form-array-row">
                                <div class="form-array-col form-array-label" [matTooltip]="field.label">{{ field.label }}</div>
                                <div class="form-array-col form-array-col-value">
                                    <app-field-input
                                            [size]="12"
                                            [sizeXl]="12"
                                            [sizeLg]="12"
                                            [formValue]="field"
                                            displayType="compact"
                                            [componentService]="componentService"
                                            [parentFormGroup]="getFieldFormGroupAt(i)"
                                            [partner]="partner">
                                    </app-field-input>
                                </div>
                            </div>
                        </ng-container>
                    </div>
                </div>
            </div>
        </ng-container>
        <ng-template #defaultTmpl>
            <ng-container *ngIf="display === 'asTitle'">
                <h3>{{ groupModel.definition.label ? groupModel.definition.label : 'Groupe' }}</h3>
                <ng-container [ngTemplateOutlet]="rowTemplate"></ng-container>
            </ng-container>
            <ng-container *ngIf="display === 'asCard'">
                <app-card [title]="groupModel.definition.label ? groupModel.definition.label : 'Groupe'">
                    <ng-container [ngTemplateOutlet]="rowTemplate"></ng-container>
                </app-card>
            </ng-container>
            <ng-template #rowTemplate>

                <div class="d-flex flex-wrap">
                    <div *ngFor="let field of fields; index as i"
                         [style.flex]="'0 0 calc(' + (platformControl.isAtLeast('LG') ? (100/groupModel.definition.nbColumns) : 100) + '%)'">
                        <app-field-input [formValue]='field' [componentService]="componentService"
                                         [label]="field?.label"
                                         [size]="12"
                                         [sizeXl]="12"
                                         [sizeLg]="12"
                                         [parentFormGroup]="getFieldFormGroupAt(i)"
                                         [partner]="partner">
                        </app-field-input>
                    </div>
                </div>
            </ng-template>
        </ng-template>
    `, styles: [`
        :host {
            display: contents;
        }`
    ]
})
export class GroupFieldsInputComponent implements OnChanges, OnDestroy {

    @Input() displayType: 'default' | 'table' = 'default';
    @Input() display: 'asCard' | 'asTitle' = 'asTitle';
    @Input() parentFormArray: UntypedFormArray;
    @Input() groupModel: FormDefinitionGroupWithValuesModel;
    @Input() componentService: FormFormSaveComponentService;
    @Input() partner: PartnerSummaryModel;

    expanded = true;
    groupFormGroup: UntypedFormGroup;
    fields: FormValueModel[] = [];
    subscriptions: Subscription[] = [];

    @ViewChildren(FieldInputComponent) fieldsComponents: QueryList<FieldInputComponent>;

    constructor(private formBuilder: UntypedFormBuilder,
                private formFormSaveComponentService: FormFormSaveComponentService,
                public platformControl: PlatformControl) {
        this.subscriptions.push(this.formFormSaveComponentService.allCardStates$.subscribe(value => this.expanded = value));
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(value => value.unsubscribe());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['groupModel'] && changes['groupModel'].currentValue) {
            this.groupFormGroup = this.formBuilder.group({
                fields: this.formBuilder.array([]),
            });
            this.parentFormArray.push(this.groupFormGroup);

            const groupFields = this.groupModel.definition.fields;
            groupFields.sort((a, b) => a.index - b.index);

            this.fields = groupFields.map(field => {
                const value = this.groupModel.values.find(value1 => value1.fieldDefinition.summary.id === field.fieldDefinition.summary.id);
                const fieldDefinition = field.fieldDefinition;
                fieldDefinition.defaultValue = field.defaultValue;
                return {
                    value: value?.value,
                    fieldDefinition: fieldDefinition,
                    label: field.label.split(':')[1]?.trim() ?? field.label,
                    id: value?.id,
                    document: value?.document,
                    comment: value?.comment
                };
            });
            groupFields.forEach(() => {
                this.fieldsControl.push(this.formBuilder.group({}));
            });
        }
    }

    get fieldsControl(): UntypedFormArray {
        return this.groupFormGroup.get('fields') as UntypedFormArray;
    }

    getFieldFormGroupAt(index: number): UntypedFormGroup {
        return this.fieldsControl.at(index) as UntypedFormGroup;
    }

    findFieldComponent(fieldId: number): FieldInputComponent {
        return this.fieldsComponents.find(fieldComponent => fieldId === fieldComponent.fieldId());
    }

    protected readonly Math = Math;
}

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        IonicModule,
        ReactiveFormsModule,
        CardModule,
        FieldInputModule,
        FormFieldModule,
        DatePickerModule,
        MatTooltipModule,
    ],
    declarations: [
        GroupFieldsInputComponent,
    ],
    exports: [
        GroupFieldsInputComponent,
        ContentDirective,
    ],
})
export class GroupFieldsInputModule {
}
